/**
 * AssetsCard — fleet-status donut for the OEM Home strip (Phase 2).
 *
 * Ports the reference HomeOEM "Assets" card (AssetDonut + legend) to RNW + Crop
 * Circle tokens. The donut is a raw <svg> (web-only, like MapView's controls) —
 * proportional segments around one circle via strokeDasharray/offset, fleet
 * total in the middle, status legend on the right.
 *
 * Map-plottable: pressing the header toggles the map to plot individual assets
 * (clustered). Consumes `useHomeCards(range)`.
 */
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tokens, Skeleton, EmptyState, ErrorState } from '@gripp/shared-ui';
import { DashboardCardShell, ViewAllLink } from './dashboardCardShell';
import { useHomeCards, type HomeAssetStatusSlice } from '../hooks/useHomeCards';
import { HomeCardProps } from '../types';

// Fleet category → Crop Circle semantic colour (matches the map markers).
// Keyed by the real status label the backend returns, not by slice index.
const CATEGORY_COLORS: Record<string, string> = {
  Ready: tokens.colors.semantic.success, // green
  'Needs Repair': tokens.colors.semantic.warning, // yellow
  'Out of Service': tokens.colors.semantic.error, // red
};
// Fallback for any unexpected label.
const CATEGORY_FALLBACK = tokens.colors.slate[400];
const categoryColor = (status: string): string =>
  CATEGORY_COLORS[status] ?? CATEGORY_FALLBACK;

// Stable display order so the legend + donut render all three categories in the
// same order even when a count is 0.
const CATEGORY_ORDER = ['Ready', 'Needs Repair', 'Out of Service'] as const;

/**
 * Returns the three categories in a stable order, filling a 0 count for any the
 * backend omitted, then appending any unexpected extra labels at the end.
 */
function orderedSlices(items: HomeAssetStatusSlice[]): HomeAssetStatusSlice[] {
  const byStatus = new Map(items.map((s) => [s.status, s]));
  const ordered: HomeAssetStatusSlice[] = CATEGORY_ORDER.map(
    (status) => byStatus.get(status) ?? { status, count: 0 }
  );
  const extras = items.filter(
    (s) => !CATEGORY_ORDER.includes(s.status as (typeof CATEGORY_ORDER)[number])
  );
  return [...ordered, ...extras];
}

// Reference AssetDonut proportions (148/46) scaled to the strip card; a thick
// ring with a small empty centre (the fleet total is the card subtitle, not a
// centre label — matching gripp-os, which renders no centre number).
const DONUT_SIZE = 140;
const DONUT_STROKE = 44;

/** Proportional donut (raw SVG, web-only). Segments drawn around one circle. */
const AssetDonut: React.FC<{
  items: HomeAssetStatusSlice[];
  total: number;
  activeStatus?: string | null;
  onSegmentPress?: (status: string) => void;
}> = ({ items, total, activeStatus, onSegmentPress }) => {
  const radius = (DONUT_SIZE - DONUT_STROKE) / 2;
  const circumference = 2 * Math.PI * radius;

  let acc = 0;
  const segments = items.map((s) => {
    const frac = total > 0 ? s.count / total : 0;
    const length = circumference * frac;
    const offset = -(circumference * (acc / Math.max(total, 1)));
    acc += s.count;
    return {
      key: s.status,
      length,
      offset,
      color: categoryColor(s.status),
    };
  });

  return (
    <View style={{ width: DONUT_SIZE, height: DONUT_SIZE }}>
      <svg
        viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
        width={DONUT_SIZE}
        height={DONUT_SIZE}
        style={{ transform: 'rotate(-90deg)' }}
      >
        <circle
          cx={DONUT_SIZE / 2}
          cy={DONUT_SIZE / 2}
          r={radius}
          fill="none"
          stroke={tokens.colors.slate[100]}
          strokeWidth={DONUT_STROKE}
        />
        {segments.map((s) => (
          <circle
            key={s.key}
            cx={DONUT_SIZE / 2}
            cy={DONUT_SIZE / 2}
            r={radius}
            fill="none"
            stroke={s.color}
            strokeWidth={DONUT_STROKE}
            strokeDasharray={`${s.length} ${circumference - s.length}`}
            strokeDashoffset={s.offset}
            // Dim non-selected segments when a filter is active.
            opacity={activeStatus && activeStatus !== s.key ? 0.3 : 1}
            onClick={
              onSegmentPress ? () => onSegmentPress(s.key) : undefined
            }
            style={onSegmentPress ? { cursor: 'pointer' } : undefined}
          />
        ))}
      </svg>
    </View>
  );
};

export const AssetsCard: React.FC<HomeCardProps> = ({
  range,
  selected,
  onToggleSelect,
  activeStatus,
  onSelectStatus,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useHomeCards(range);
  const assets = data?.assets;

  const title = t('home.assets.title', 'Assets');
  const action = (
    <ViewAllLink
      label={t('home.cards.viewAll', 'View all')}
      onPress={() => navigate('/assets')}
      testID="assets-card-view-all"
    />
  );

  if (isLoading) {
    return (
      <DashboardCardShell title={title} action={action} testID="assets-card"
        loadingSlot={<Skeleton height={120} radius={tokens.borderRadius.full} style={styles.loadingDonut} />}
      >
        {null}
      </DashboardCardShell>
    );
  }
  if (error) {
    return (
      <DashboardCardShell title={title} action={action} testID="assets-card"
        errorSlot={<ErrorState title={t('home.cards.error', 'Could not load data')} testID="assets-card-error" />}
      >
        {null}
      </DashboardCardShell>
    );
  }
  if (!assets || assets.total === 0) {
    return (
      <DashboardCardShell title={title} action={action} testID="assets-card"
        emptySlot={<EmptyState title={t('home.assets.empty', 'No assets for this period')} testID="assets-card-empty" />}
      >
        {null}
      </DashboardCardShell>
    );
  }

  // Stable three-category order, zero-filling any the backend omitted.
  const slices = orderedSlices(assets.items);

  return (
    <DashboardCardShell
      title={title}
      subtitle={String(assets.total)}
      action={action}
      testID="assets-card"
      onHeaderPress={onToggleSelect}
      selected={selected}
      headerPressLabel={t('home.assets.plot', 'Plot assets on the map')}
    >
      <View style={styles.body}>
        <AssetDonut
          items={slices}
          total={assets.total}
          activeStatus={activeStatus}
          onSegmentPress={onSelectStatus}
        />
        <View style={styles.legend}>
          {slices.map((s) => {
            const isActive = activeStatus === s.status;
            return (
              <Pressable
                key={s.status}
                style={[
                  styles.legendRow,
                  isActive && styles.legendRowActive,
                  // Dim non-selected rows when a filter is active.
                  activeStatus && !isActive ? styles.legendRowDimmed : null,
                ]}
                onPress={() => onSelectStatus?.(s.status)}
                accessibilityRole="button"
                accessibilityLabel={t('home.assets.filterBy', 'Filter map by {{status}}', { status: s.status })}
              >
                <View
                  style={[
                    styles.legendDot,
                    { backgroundColor: categoryColor(s.status) },
                  ]}
                />
                <Text
                  style={[styles.legendLabel, isActive && styles.legendLabelActive]}
                  numberOfLines={1}
                >
                  {s.status}
                </Text>
                <Text style={styles.legendCount}>{s.count}</Text>
              </Pressable>
            );
          })}
        </View>
      </View>
    </DashboardCardShell>
  );
};

AssetsCard.displayName = 'AssetsCard';

const styles = StyleSheet.create({
  body: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.lg,
  },
  loadingDonut: {
    alignSelf: 'center',
    width: 140,
  },
  legend: {
    flex: 1,
    gap: 6,
    minWidth: 0,
  },
  legendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingVertical: 2,
    paddingHorizontal: tokens.spacing.xs,
    marginHorizontal: -tokens.spacing.xs,
    borderRadius: tokens.borderRadius.sm,
  },
  legendRowActive: {
    backgroundColor: tokens.colors.slate[100],
  },
  legendRowDimmed: {
    opacity: 0.45,
  },
  legendLabelActive: {
    color: tokens.colors.neutral[800],
    fontWeight: tokens.typography.label.mediumEmphasised.fontWeight,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: tokens.borderRadius.full,
  },
  legendLabel: {
    ...tokens.typography.label.medium,
    color: tokens.colors.slate[400],
    flex: 1,
  },
  legendCount: {
    ...tokens.typography.label.medium,
    fontWeight: '600',
    color: tokens.colors.neutral[800],
    fontVariant: ['tabular-nums'],
  },
});
